import React, {useContext, useEffect, useState} from "react"
import style from "../UserLicenseManagement/style.less"
import Badge from "../Badge"
import Button from "../Button"
import Divider from "../Divider"
import {
  addUserLicense,
  deactivateLicense,
  finishLicense,
  getLicenseTypes,
  patchUserLicense,
  LicenseType,
  User,
  UserLicense
} from "../../services/api/api"
import {BadgeType} from "../Badge/Badge"
import {FormattedMessage, useIntl} from "react-intl"
import WarningDialog from "../WarningDialog"
import {ToastContext, ToastContextType} from "../../context"
import CancelIcon from '@mui/icons-material/Cancel'
import GradingIcon from '@mui/icons-material/Grading';
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import IconButton from '@mui/material/IconButton'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import {useAuth0} from "@auth0/auth0-react";
import {isAuth0DsAdmin, isAuth0SynpliAdmin} from "@yaak/ds-admin/src/utils/roles";
import { formatTrainingPathLabel, getTrainingPathsForLicense } from '../../helpers/trainingPathData'


type UserLicenseManagementProps = {
  token: string
  studentUser: User
  licenses: Array<UserLicense>
  onLicenseAdded?: () => void
}

const UserLicenseManagement: React.FunctionComponent<UserLicenseManagementProps> = ({
  token,
  studentUser,
  licenses,
  onLicenseAdded
}) => {

  const { user } = useAuth0()

  const [isCancelDialogOpen, setCancelDialogOpen] = React.useState(false)
  const [isFinishDialogOpen, setFinishDialogOpen] = React.useState(false)
  const [isAddLicenseDialogOpen, setAddLicenseDialogOpen] = React.useState(false)
  const [currentLicense, setCurrentLicense] = React.useState<UserLicense | null>(null)
  const [availableLicenseTypes, setAvailableLicenseTypes] = useState<LicenseType[]>([])
  const [selectedLicenseTypeId, setSelectedLicenseTypeId] = useState<string>('')
  const [selectedLearningPathId, setSelectedLearningPathId] = useState<string>('')
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [showLicenceManagement, setShowLicenceManagement] = useState<boolean>(false)
  const [editingLicenseId, setEditingLicenseId] = useState<string | null>(null)
  const [editTrainingPathId, setEditTrainingPathId] = useState<string>('')
  const [isSavingEdit, setIsSavingEdit] = useState<boolean>(false)

  const { setShowToast } = useContext(ToastContext) as ToastContextType
  const intl = useIntl()
  const trainingPathLabel = (trainingPathId: string) =>
    formatTrainingPathLabel(intl, trainingPathId)
  useEffect(() => {
    const fetchLicenseTypes = async () => {
      try {
        const licenseTypes = await getLicenseTypes({ token, onAlert: setShowToast })
        setAvailableLicenseTypes(licenseTypes)
      } catch (error) {
        console.error('Failed to fetch license types:', error)
      }
    }

    fetchLicenseTypes()
  }, [token, setShowToast])

  useEffect(() => {
    const isDsAdminUser = isAuth0DsAdmin(user) || isAuth0SynpliAdmin(user)
    setShowLicenceManagement(isDsAdminUser)
  }, [user]);


  const onFinishLicense = () => {
    if (currentLicense) {
      finishLicense({token, onAlert: setShowToast, userId: studentUser.id, userLicenseId: currentLicense.id})
        .then(() => {
          currentLicense.finishedAt = new Date().toISOString()
          setFinishDialogOpen(false)
          setCurrentLicense(null)
          onLicenseAdded?.()
        })
    }
  }

  const onCancelLicense = () => {
    if (currentLicense) {
      deactivateLicense({token, onAlert: setShowToast, userId: studentUser.id, userLicenseId: currentLicense.id})
        .then(() => {
          currentLicense.disabledAt = new Date().toISOString()
          setCancelDialogOpen(false)
          setCurrentLicense(null)
          onLicenseAdded?.()
        })
    }
  }

  const handleAddLicense = async () => {
    if (!selectedLicenseTypeId) return
    if (!selectedLearningPathId) return

    setIsSubmitting(true)
    try {
      await addUserLicense({
        token,
        userId: studentUser.id,
        licenseTypeId: selectedLicenseTypeId,
        learningPathId: selectedLearningPathId,
        onAlert: setShowToast,
      })

      setAvailableLicenseTypes((prev) => prev.filter((type) => type.id !== selectedLicenseTypeId))
      setAddLicenseDialogOpen(false)
      setSelectedLicenseTypeId('')
      setSelectedLearningPathId('')
      onLicenseAdded?.()
    } catch (error) {
      console.error('Failed to add license:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleLicenseTypeChange = (licenseTypeId: string) => {
    setSelectedLicenseTypeId(licenseTypeId)
    setSelectedLearningPathId('')
    const licenseCode = availableLicenseOptions.find((lt) => lt.id === licenseTypeId)?.code ?? ''
    if (licenseCode) {
      const paths = getTrainingPathsForLicense(licenseCode)
      if (paths.length === 1) setSelectedLearningPathId(paths[0].id)
    }
  }

  const startEditLicense = (license: UserLicense) => {
    setEditingLicenseId(license.id)
    setEditTrainingPathId(license.trainingPathId ?? '')
  }

  const cancelEditLicense = () => {
    setEditingLicenseId(null)
    setEditTrainingPathId('')
  }

  const saveEditLicense = async (license: UserLicense) => {
    if (!editTrainingPathId) return
    setIsSavingEdit(true)
    try {
      await patchUserLicense({
        token,
        onAlert: setShowToast,
        userId: studentUser.id,
        userLicenseId: license.id,
        trainingPathId: editTrainingPathId,
      })
      cancelEditLicense()
      onLicenseAdded?.()
    } finally {
      setIsSavingEdit(false)
    }
  }

  const handleDialogClose = () => {
    setAddLicenseDialogOpen(false)
    setSelectedLicenseTypeId('')
    setSelectedLearningPathId('')
    setIsSubmitting(false)
  }

  // Get already assigned license IDs to filter them out from the dropdown
  const assignedLicenseIds = licenses.map((license) => license.licenseId)
  const availableLicenseOptions = availableLicenseTypes
    .filter((licenseType) => !assignedLicenseIds.includes(licenseType.id))

  return (
    <>
     <div className={style.contentBox}>
       <h3 className={style.contentBoxTitle}><FormattedMessage id="license_type_classes" defaultMessage="License Type Classes" /></h3>
       <div className={style.licenseChangeHint}>
         <InfoOutlinedIcon className={style.licenseChangeHintIcon} fontSize="small" />
         <span><FormattedMessage id="license_change_hint" /></span>
       </div>
       { licenses.map(function(license, index) {
         const badgeType = BadgeType.green
         const badgeLabel = license.finishedAt !== null ? <FormattedMessage id={"license_finished"}/> : <FormattedMessage id={"license_active"}/>
         const isEditing = editingLicenseId === license.id
         const availableEditPaths = getTrainingPathsForLicense(license.licenseCode)
         return (<>
           <div className={style.row} key={license.id}>
             {isEditing ? (
               <div className={style.editRow}>
                 <span className={style.licenseCode}>{license.licenseCode}</span>
                 <FormControl size="small" className={style.selectControl}>
                   <Select
                     value={editTrainingPathId}
                     onChange={(e) => setEditTrainingPathId(e.target.value)}
                     displayEmpty
                   >
                     {availableEditPaths.map((entry) => (
                       <MenuItem key={entry.id} value={entry.id}>
                         {trainingPathLabel(entry.id)}
                       </MenuItem>
                     ))}
                   </Select>
                 </FormControl>
                 <div className={style.editActionButtons}>
                   <IconButton
                     size="small"
                     onClick={() => saveEditLicense(license)}
                     disabled={!editTrainingPathId || isSavingEdit}
                     title={intl.formatMessage({ id: 'save_changes' })}
                     className={style.confirmButton}
                   >
                     <CheckIcon fontSize="small" />
                   </IconButton>
                   <IconButton
                     size="small"
                     onClick={cancelEditLicense}
                     title={intl.formatMessage({ id: 'button_cancel' })}
                     className={style.removeButton}
                   >
                     <CloseIcon fontSize="small" />
                   </IconButton>
                 </div>
               </div>
             ) : (
               <div className={style.licenseInfo}>
                 <span className={style.licenseCode}>{license.licenseCode}</span>
                 {license.trainingPathId && (
                   <span className={style.licenseMeta}>
                     <span className={style.licenseSeparator}>·</span>
                     <span>{trainingPathLabel(license.trainingPathId)}</span>
                   </span>
                 )}
               </div>
             )}
             {!isEditing && <div className={style.badgeWrapper}><Badge type={badgeType} label={badgeLabel}/></div>}
             { showLicenceManagement && !isEditing && (
               <div className={style.editButtonWrapper}>
                 { license.finishedAt === null && (
                   <>
                      <Button
                        secondary
                        onClick={() => startEditLicense(license)}
                        title={intl.formatMessage({ id: 'button_edit', defaultMessage: 'Edit' })}
                        icon={<EditIcon />}
                      />
                      <Button
                       className={style.cancelButton}
                       tertiary
                       onClick={() => {
                         setCurrentLicense(license)
                         setCancelDialogOpen(true)
                       }}
                       title={intl.formatMessage({ id: 'button_cancel' })}
                       icon={<CancelIcon />}
                      />
                      <Button
                         onClick={() => {
                           setCurrentLicense(license)
                           setFinishDialogOpen(true)
                         }}
                         title={intl.formatMessage({ id: 'mark_as_finished' })}
                         icon={<GradingIcon />}
                      />
                   </>
                 )}
               </div>
             )}
           </div>
           <Divider className={style.classDivider}/></>
       )})}
       { showLicenceManagement && (
         <div className={style.addMoreWrapper}>
           <Button
             text={intl.formatMessage({id: "add_new"})}
             onClick={() => setAddLicenseDialogOpen(true)}
             icon={<AddIcon />}
           />
         </div>
       )}
     </div>
      <WarningDialog
        isOpen={isCancelDialogOpen}
        dialogContentText={intl.formatMessage({id: "cancel_license_warning", defaultMessage: "Are you sure you want to cancel this license? The instructor will not be able to record drives on it anymore."})}
        dialogTitle={''}
        onSubmit={onCancelLicense}
        onCancel={() => {
          setCancelDialogOpen(false)
          setCurrentLicense(null)
        }}
        buttons={{
          cancel: intl.formatMessage({id: "button_cancel", defaultMessage: "Cancel"}),
          submit: intl.formatMessage({id: "mark_as_cancelled", defaultMessage: "Mark as Cancelled"}),
        }}
      />
      <WarningDialog
        isOpen={isFinishDialogOpen}
        dialogContentText={intl.formatMessage({id: "finish_license_warning", defaultMessage: "Are you sure you want to mark the license as finished? The instructor will not be able to record drives on it anymore."})}
        dialogTitle={''}
        onSubmit={onFinishLicense}
        onCancel={() => {
          setFinishDialogOpen(false)
          setCurrentLicense(null)
        }}
        buttons={{
          cancel: intl.formatMessage({id: "button_cancel", defaultMessage: "Cancel"}),
          submit: intl.formatMessage({id: "mark_as_finished", defaultMessage: "Mark as Finished"}),
        }}
      />
      <Dialog
        open={isAddLicenseDialogOpen}
        onClose={handleDialogClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: '1rem',
            padding: '1rem'
          }
        }}
      >
        <DialogTitle>
          <FormattedMessage id="add_license" defaultMessage="Add License" />
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal" className={style.selectControl}>
            <InputLabel id="license-type-select-label">
              <FormattedMessage id="license_type" defaultMessage="License Type" />
            </InputLabel>
            <Select
              labelId="license-type-select-label"
              value={selectedLicenseTypeId}
              label={<FormattedMessage id="license_type" defaultMessage="License Type" />}
              onChange={(e) => handleLicenseTypeChange(e.target.value)}
            >
              {availableLicenseOptions.map((licenseType) => (
                <MenuItem key={licenseType.id} value={licenseType.id}>
                  {licenseType.code}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal" className={style.selectControl}>
            <InputLabel id="license-path-select-label">
              <FormattedMessage id="license_training_path_placeholder" defaultMessage="Training Path" />
            </InputLabel>
            <Select
              labelId="license-path-select-label"
              disabled={!selectedLicenseTypeId}
              value={selectedLearningPathId}
              label={<FormattedMessage id="license_training_path_placeholder" defaultMessage="Training Path" />}
              onChange={(e) => setSelectedLearningPathId(e.target.value)}
            >
              {getTrainingPathsForLicense(
                availableLicenseOptions.find((lt) => lt.id === selectedLicenseTypeId)?.code ?? ''
              ).map((entry) => (
                <MenuItem key={entry.id} value={entry.id}>
                  {trainingPathLabel(entry.id)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center' }}>
          <Button
            text={intl.formatMessage({id: "button_cancel", defaultMessage: "Cancel"})}
            onClick={handleDialogClose}
            tertiary
          />
          <Button
            text={intl.formatMessage({id: "add_license", defaultMessage: "Add License"})}
            onClick={handleAddLicense}
            disabled={!selectedLicenseTypeId || !selectedLearningPathId || isSubmitting}
          />
        </DialogActions>
      </Dialog>
    </>
 )
}

export default UserLicenseManagement
